import pluralize from 'pluralize';

import {
  UTILITY_ACTIVATION_STATUS_CANCELED,
  UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED,
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  UTILITY_ACTIVATION_UI_STATE_PENDING,
  UTILITY_ACTIVATION_UI_STATE_WARNING,
  UTILITY_ACTIVATION_UI_STATE_COMPLETED,
  UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED,
  UTILITY_ACTIVATION_UI_STATE_TO_COLOR_MAPPING,
  UTILITY_ACTIVATION_UI_STATE_TO_ICON_MAPPING,
  UTILITY_ACTIVATION_FEEDBACK_TAGS_LOW_RATING,
  UTILITY_ACTIVATION_FEEDBACK_TAGS_HIGH_RATING,
  UTILITY_PARTNER_CUSTOMER_TYPE_HOMEBUYER,
  UTILITY_PARTNER_CUSTOMER_TYPE_PROPERTY_MANAGER,
} from 'common/utility/constants';

import { hasRoleAdmin, UTILITY_USER_STATUS_ACTIVE } from 'common/utility/userRoles';

export const getRenterUtilityLink = ({ partnerSlug, shortId }) => {
  return `/utility-setup/${partnerSlug}/${shortId}`;
};

export const getProviderPlanPriceText = ({ price, unit }) => {
  return `$${parseFloat(price).toFixed(2)}${unit ? `/${unit}` : ''}`;
};

export const getSupportSmsBody = ({ addressStreetDisplay }) => {
  if (global.encodeURIComponent) {
    const text = `Hi, I have a question about the utility setup for ${addressStreetDisplay}`;
    return global.encodeURIComponent(text);
  }
};

export const getActivationItemStatusUiState = ({
  status,
  defaultState = UTILITY_ACTIVATION_UI_STATE_PENDING,
  completedStates = [
    UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
    UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  ],
  warningStates = [UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED],
  label: initialLabel,
  utilityProviders = [],
}) => {
  const uiState = !status
    ? UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED
    : completedStates.includes(status)
    ? UTILITY_ACTIVATION_UI_STATE_COMPLETED
    : warningStates.includes(status)
    ? UTILITY_ACTIVATION_UI_STATE_WARNING
    : defaultState;

  const label = `${initialLabel}${
    !status
      ? ` (not required) ${
          utilityProviders.length > 0
            ? `${pluralize('provider', utilityProviders.length, true)} mapped`
            : ''
        }`
      : status === UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED
      ? ' (PURCHASE REQUESTED)'
      : ''
  }`;

  return {
    color: UTILITY_ACTIVATION_UI_STATE_TO_COLOR_MAPPING[uiState],
    iconUrl: UTILITY_ACTIVATION_UI_STATE_TO_ICON_MAPPING[uiState],
    label,
    uiState,
  };
};

export const getEntityOwnershipText = ({ name, word }) => {
  const nameWithApostrophe = name.endsWith('s') ? `${name}'` : `${name}'s`;
  return `${nameWithApostrophe} ${word}`;
};

export const isUtilityActivationActive = (utilityActivation) => {
  if (!utilityActivation?.status) {
    return false;
  }

  return (
    utilityActivation.status !== UTILITY_ACTIVATION_STATUS_CANCELED && !utilityActivation.archivedAt
  );
};

export const getUtilityActivationFeedbackTags = (rating) => {
  return rating < 5
    ? UTILITY_ACTIVATION_FEEDBACK_TAGS_LOW_RATING
    : UTILITY_ACTIVATION_FEEDBACK_TAGS_HIGH_RATING;
};

export const isEmailValid = (email) => {
  if (!email) {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const canAccessActivation = ({ utilityActivation, utilityUser }) => {
  const isActivationClaimed = utilityActivation.user?.status === UTILITY_USER_STATUS_ACTIVE;
  const isAdminUser = hasRoleAdmin(utilityUser.roles);

  if (!isActivationClaimed) {
    return true;
  }

  if (isAdminUser) {
    return true;
  }

  return utilityActivation.user.id === utilityUser.id;
};

export const isHomebuyerPartner = (utilityPartner) =>
  utilityPartner?.customerType === UTILITY_PARTNER_CUSTOMER_TYPE_HOMEBUYER;

export const shouldViewTenantLiabilityBanners = (utilityPartner) =>
  utilityPartner?.experienceConfiguration?.tenantLiabilityBanner === true &&
  utilityPartner?.experienceConfiguration?.liabilityInsuranceEnabled !== true &&
  utilityPartner?.customerType === UTILITY_PARTNER_CUSTOMER_TYPE_PROPERTY_MANAGER;
